import { inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { SopoLoadingService } from '../sopo-loading/sopo-loading.service';
import { SopoMenuService } from '../sopoMenu/sopo-menu.service';
import SplashScreenElectron from 'src/app/plugins/splash-screen.plugin';

const SPLASH_UPDATE_TIMEOUT = 8000;

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
    private _isUpdateAvailableSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _sopoMenuService = inject(SopoMenuService);
    private _handledBySplash = false;

    constructor(
        private updates: SwUpdate,
        private loadingService: SopoLoadingService,
        private sopoPopupService: SopoPopupService,
    ) {
        if (updates.isEnabled) {
            updates.versionUpdates.pipe(
                filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
            ).subscribe((evt) => {
                if (this._handledBySplash) return;
                this.promtForUpdateConfirm();
            });

            updates.unrecoverable.subscribe(event => {
                this.showReloadAlert();
            })
        }
    }

    private showReloadAlert() {
        this.sopoPopupService.showAlert('app', {
            backdropDismiss: false,
            header: 'Unrecoverable state',
            message: 'Please reload the page.',
        });
    }

    promtForUpdateConfirm() {
        if (!this.updates.isEnabled) return;

        this._sopoMenuService.closeMenu();
        this.sopoPopupService.promptForConfirmation(
            'app',
            {
                data: {
                    header: 'Update available',
                    body: 'Do you want to load the new version?',
                    okText: 'Reload',
                    cancelText: 'Cancel',
                }
            },
        ).then(answer => {
            if (answer) {
                this.loadingService.present('update');
                this.updates.activateUpdate().then(() => {
                    this.loadingService.dismiss('update');
                    document.location.reload();
                });
            } else {
                if (!this._isUpdateAvailableSubject.getValue())
                    this._isUpdateAvailableSubject.next(true);
            }
        });
    }

    checkForUpdates() {
        if (this.updates.isEnabled) {
            this.updates.checkForUpdate();
        }
    }

    async checkForSplashUpdate(): Promise<boolean> {
        if (!this.updates.isEnabled) return false;

        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
            console.debug('[UpdateService] No SW registered yet (first visit), skipping update check');
            return false;
        }

        this._handledBySplash = true;

        try {
            const TIMED_OUT = Symbol('timeout');
            const timeout = new Promise<typeof TIMED_OUT>(resolve => setTimeout(() => resolve(TIMED_OUT), SPLASH_UPDATE_TIMEOUT));
            const check = this.updates.checkForUpdate();
            const result = await Promise.race([check, timeout]);

            if (result === TIMED_OUT) {
                console.debug('[UpdateService] SW update check timed out after %dms, dismissing splash', SPLASH_UPDATE_TIMEOUT);
                this._handledBySplash = false;
                return false;
            }

            if (!result) {
                console.debug('[UpdateService] No update available, dismissing splash');
                this._handledBySplash = false;
                return false;
            }

            console.debug('[UpdateService] Update found, transitioning splash to update UI');
            await SplashScreenElectron.transitionToUpdate();
            await SplashScreenElectron.showActivatingState();
            await this.updates.activateUpdate();
            console.debug('[UpdateService] Update activated, showing restart prompt');
            await SplashScreenElectron.showRestartPrompt();
            return true;
        } catch (err) {
            console.error('[UpdateService] Splash update check failed:', err);
            this._handledBySplash = false;
            return false;
        }
    }

    isUpdateAvailable(): Observable<boolean> {
        return this._isUpdateAvailableSubject.asObservable();
    }
}
